import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Provider, useStore } from 'react-redux';
import Grid from '../../../components/partials/Grid';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import { parseCSVToGrid } from '../../../utils/csvUtils';
import { MAX_DISPLAY_ROWS } from '../../constants';
import ArtifactLoader from './ArtifactLoader';
import css from './GridArtifact.css';

const GridArtifactInner = ({ data, metadata, gridId }) => {
    const store = useStore();

    const { rowData, columnDefs, truncationInfo } = useMemo(() => {
        if (!data || typeof data !== 'string') {
            return { rowData: [], columnDefs: [], truncationInfo: null };
        }

        const lines = data.split('\n');
        const totalRows = Math.max(0, lines.length - 1);
        let csvToRender = data;
        let truncInfo = null;

        if (totalRows > MAX_DISPLAY_ROWS) {
            csvToRender = lines.slice(0, MAX_DISPLAY_ROWS + 1).join('\n');
            truncInfo = {
                is_truncated: true,
                truncation_message: `Showing first ${MAX_DISPLAY_ROWS} of ${totalRows} rows for performance`,
                display_row_count: MAX_DISPLAY_ROWS,
                row_count: totalRows,
            };
        }

        return { ...parseCSVToGrid(csvToRender), truncationInfo: truncInfo };
    }, [data]);

    const displayMetadata = truncationInfo || metadata;

    if (!rowData || rowData.length === 0) {
        return (
            <div className={css.emptyState}>
                <Icon name="table" size={IconSizes.LARGE} />
                <div>No data available</div>
            </div>
        );
    }

    return (
        <div className={css.gridArtifact}>
            <div className={css.gridWrapper}>
                <Provider store={store}>
                    <Grid
                        id={gridId}
                        key={gridId}
                        columnDefs={columnDefs}
                        rowData={rowData}
                        reportType="reports"
                        queryObject={{}}
                        columnTypes={{ dimension: {}, metric: {} }}
                        hideResetTable
                        hidePivotHeaders
                        hideColumnsSideBar
                        disableScrollingHooks
                        disableUrlSync
                        heightOverride={500}
                    />
                </Provider>
            </div>
            {displayMetadata?.is_truncated && (
                <div className={css.truncationWarning}>
                    <Icon name="info-circle" variant={IconVariants.WARNING} size={IconSizes.SMALL} />
                    <span>
                        {displayMetadata.truncation_message ||
                            `Showing ${displayMetadata.display_row_count} of ${displayMetadata.row_count} rows`}
                    </span>
                </div>
            )}
        </div>
    );
};

const GridArtifact = ({ artifact }) => (
    <ArtifactLoader artifact={artifact}>
        {data => (
            <GridArtifactInner
                data={data}
                metadata={artifact.metadata}
                gridId={`artifact-grid-${artifact.id}`}
            />
        )}
    </ArtifactLoader>
);

GridArtifact.propTypes = {
    artifact: PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        data: PropTypes.string,
        content_url: PropTypes.string,
        metadata: PropTypes.shape({
            display_row_count: PropTypes.number,
            is_truncated: PropTypes.bool,
            row_count: PropTypes.number,
            truncation_message: PropTypes.string,
            columns: PropTypes.arrayOf(PropTypes.string),
        }),
    }).isRequired,
};

export default GridArtifact;
